import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CheckCircle2, AlertTriangle, Loader2, Circle } from "lucide-react";

const TYPE_LABEL = {
  road: "Road",
  osr: "OSR",
  footpath: "Footpath",
  dumping_yard: "Dumping Yard",
  sipcot_po_office: "Project Office",
};

const STATE_TONE = {
  not_started: "border-slate-200 bg-slate-50 text-slate-500",
  in_progress: "border-amber-300 bg-amber-50 text-amber-900",
  complete:    "border-emerald-400 bg-emerald-50 text-emerald-900",
  overdue:     "border-rose-400 bg-rose-50 text-rose-900",
};

const STATE_ICON = {
  not_started: Circle,
  in_progress: Loader2,
  complete: CheckCircle2,
  overdue: AlertTriangle,
};

function toDdMmYyyy(iso) {
  if (!iso) return null;
  const [y, m, d] = String(iso).split("-");
  if (!y || !m || !d) return iso;
  return `${d}.${m}.${y}`;
}

function fmtKm(n) {
  if (n == null) return "";
  return Number(n).toFixed(1);
}

function sortTiles(tiles) {
  const order = { overdue: 0, in_progress: 1, not_started: 2, complete: 3 };
  return [...tiles].sort((a, b) => {
    const so = (order[a.state] ?? 9) - (order[b.state] ?? 9);
    if (so !== 0) return so;
    return (b.neglect_days ?? -1) - (a.neglect_days ?? -1);
  });
}

export function AssetConstellationMosaic({ tiles, onTileClick, dense = false, hideHeaders = false }) {
  if (!tiles || tiles.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center rounded-lg border border-dashed border-slate-200 bg-slate-50/50 text-sm text-slate-500" data-testid="asset-constellation-mosaic-empty">
        No assets seeded for this view.
      </div>
    );
  }
  const sorted = sortTiles(tiles);
  // Group by category (asset_type) only when there is a mix; otherwise flat grid.
  const types = Array.from(new Set(sorted.map((t) => t.asset_type)));
  const groups = types.map((tp) => ({ type: tp, items: sorted.filter((t) => t.asset_type === tp) }));
  const showHeaders = !hideHeaders && types.length > 1;

  return (
    <TooltipProvider delayDuration={120}>
      <div className="flex flex-col gap-4" data-testid="asset-constellation-mosaic">
        {hideHeaders ? (
          <div
            className={cn(
              "grid gap-1.5",
              dense
                ? "grid-cols-[repeat(auto-fill,minmax(28px,1fr))]"
                : "grid-cols-[repeat(auto-fill,minmax(116px,1fr))]"
            )}
          >
            {sorted.map((t) => (
              <Tile key={t.asset_id} tile={t} onClick={onTileClick} dense={dense} />
            ))}
          </div>
        ) : groups.map((g) => (
          <div key={g.type} className="flex flex-col gap-2">
            {showHeaders ? (
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.12em] text-slate-500">
                <span className="font-semibold">{TYPE_LABEL[g.type] || g.type}</span>
                <span className="text-slate-400">·</span>
                <span>{g.items.length} asset{g.items.length === 1 ? "" : "s"}</span>
                <div className="ml-2 h-px flex-1 bg-slate-200" />
              </div>
            ) : null}
            <div
              className={cn(
                "grid gap-2",
                dense
                  ? "grid-cols-[repeat(auto-fill,minmax(56px,1fr))]"
                  : "grid-cols-[repeat(auto-fill,minmax(116px,1fr))]"
              )}
            >
              {g.items.map((t) => (
                <Tile key={t.asset_id} tile={t} onClick={onTileClick} dense={dense} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </TooltipProvider>
  );
}

function Tile({ tile, onClick, dense }) {
  const tone = STATE_TONE[tile.state] || STATE_TONE.not_started;
  const Icon = STATE_ICON[tile.state] || Circle;
  const isRoad = tile.asset_type === "road";
  const pct = Math.max(0, Math.min(100, Math.round((tile.progress || 0) * 100)));
  const tip = (
    <div className="max-w-[260px] space-y-1 text-xs">
      <div className="font-semibold text-foreground">{tile.name}</div>
      <div className="text-muted-foreground">{TYPE_LABEL[tile.asset_type] || tile.asset_type}</div>
      {isRoad && tile.length_km ? (
        <div className="font-mono">{fmtKm(tile.cleaned_km)} / {fmtKm(tile.length_km)} km · {pct}%</div>
      ) : (
        <div className="font-mono">{tile.visits} visit{tile.visits === 1 ? "" : "s"} this cycle</div>
      )}
      <div>Last cleaned: <span className="font-mono">{toDdMmYyyy(tile.last_date) || "—"}</span></div>
      {tile.neglect_days != null ? (
        <div className={tile.neglect_days >= 14 ? "text-rose-700" : "text-slate-600"}>
          Idle <span className="font-mono">{tile.neglect_days}</span> day{tile.neglect_days === 1 ? "" : "s"}
        </div>
      ) : null}
    </div>
  );
  if (dense) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            onClick={() => onClick && onClick(tile)}
            className={cn(
              "relative aspect-square overflow-hidden rounded-[10px] border focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500",
              tone,
              tile.state === "overdue" ? "animate-pulse" : ""
            )}
            data-testid={`asset-tile-button-${tile.asset_id}`}
            data-state={tile.state}
            aria-label={`${tile.name}, ${tile.state}`}
          >
            {isRoad && tile.length_km ? (
              <div className="absolute inset-x-0 bottom-0 bg-emerald-500/30" style={{ height: `${pct}%` }} />
            ) : null}
            <Icon className={cn("absolute right-0.5 top-0.5 h-2.5 w-2.5", tile.state === "in_progress" ? "animate-spin" : "")} />
            {tile.state === "in_progress" || tile.state === "complete" ? (
              <span className="absolute inset-x-0 bottom-0.5 text-center font-mono text-[8px] leading-none tabular-nums">{pct}%</span>
            ) : null}
          </button>
        </TooltipTrigger>
        <TooltipContent side="top" className="bg-white" data-testid={`asset-tile-tooltip-${tile.asset_id}`}>{tip}</TooltipContent>
      </Tooltip>
    );
  }
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={() => onClick && onClick(tile)}
          className={cn(
            "group relative flex aspect-[5/4] flex-col items-stretch justify-between overflow-hidden rounded-[14px] border-2 px-3 py-2 text-left transition-transform duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 hover:-translate-y-0.5 hover:shadow-md",
            tone
          )}
          data-testid={`asset-tile-button-${tile.asset_id}`}
          data-state={tile.state}
          aria-label={`${tile.name}, ${tile.state}`}
        >
          {/* Road km fill */}
          {isRoad && tile.length_km ? (
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-emerald-500/15" style={{ height: `${pct}%` }} />
          ) : null}
          <div className="relative z-10 flex items-start justify-between gap-1">
            <span className="line-clamp-2 text-[12px] font-medium leading-tight">{tile.name}</span>
            <Icon className={cn("h-4 w-4 shrink-0", tile.state === "in_progress" ? "animate-spin" : "")} />
          </div>
          <div className="relative z-10 flex items-center justify-between gap-1 text-[11px]">
            {isRoad && tile.length_km ? (
              <span className="font-mono" data-testid={`asset-road-km-text-${tile.asset_id}`}>{fmtKm(tile.cleaned_km)} / {fmtKm(tile.length_km)} km</span>
            ) : (
              <span className="text-[10px] uppercase tracking-wider opacity-75">{TYPE_LABEL[tile.asset_type] || tile.asset_type}</span>
            )}
            <span className="font-mono" data-testid={`asset-tile-state-${tile.asset_id}`}>{pct}%</span>
          </div>
        </button>
      </TooltipTrigger>
      <TooltipContent side="top" className="bg-white" data-testid={`asset-tile-tooltip-${tile.asset_id}`}>{tip}</TooltipContent>
    </Tooltip>
  );
}
